import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import getLang from 'I18n/utils/getLang';
import { KEY_PREFIX } from 'marketplace-ui-common/hooks/useSearchParams';
import { getIsPrerenderUserAgent } from 'marketplace-ui-common/utils/userAgent';
import { useUnifiedDetails } from 'marketplace-ui-details/hooks/useUnifiedDetails';
import { useMemo } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Redirect, generatePath, useLocation } from 'quartz-routing/react-router';
import { StorefrontPageMetadata } from '../../components/StorefrontPageMetadata';
import { Paths } from '../../constants/paths';
import { getFullPublicUrl } from '../../utils/urls';
import { useFilterConfigData } from '../../filters/hooks/useFilterConfig';
import { useParsedSearchResultsPathParams } from '../hooks/useParsedSearchResultsPathParams';
import { jsx as _jsx } from "react/jsx-runtime";
function PrerenderLegacyListingRedirect({
  listingSlug
}) {
  const listingPath = generatePath(Paths.LISTING_DETAILS, {
    listingSlug
  });
  const appContext = useAppContext();
  const {
    unifiedDetailsLoading,
    unifiedDetailsError
  } = useUnifiedDetails({
    appContext,
    slug: listingSlug,
    language: getLang()
  });
  if (unifiedDetailsLoading) {
    return null;
  }
  if (!unifiedDetailsError) {
    return /*#__PURE__*/_jsx(StorefrontPageMetadata, {
      prerenderStatusCode: 301,
      prerenderHeaders: {
        Location: getFullPublicUrl(listingPath)
      }
    });
  }

  // Listing not found (or error): redirect to the listing page so
  // ListingDetails can render the appropriate 404/error state for Prerender.
  return /*#__PURE__*/_jsx(Redirect, {
    to: listingPath
  });
}
export function ProductsSearchResultsProxy({
  productType
}) {
  var _data$result, _data$result2;
  const parsedPathParams = useParsedSearchResultsPathParams();
  const location = useLocation();
  const {
    data,
    loading
  } = useFilterConfigData();
  const CATEGORY = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 || (_data$result = _data$result.filterConfigs) === null || _data$result === void 0 ? void 0 : _data$result.CATEGORY;
  const AGENT_TEAM = data === null || data === void 0 || (_data$result2 = data.result) === null || _data$result2 === void 0 || (_data$result2 = _data$result2.filterConfigs) === null || _data$result2 === void 0 ? void 0 : _data$result2.AGENT_TEAM;
  const {
    pathname,
    params,
    isLegacyListingRedirect
  } = useMemo(() => {
    if (loading) {
      return {
        pathname: Paths.SEARCH_RESULTS,
        params: new URLSearchParams(),
        isLegacyListingRedirect: false
      };
    }
    const urlParams = new URLSearchParams(location.search);
    if (productType === 'APP' && parsedPathParams.categorySlug) {
      var _CATEGORY$options$fin;
      const categoryEnumValue = CATEGORY === null || CATEGORY === void 0 || (_CATEGORY$options$fin = CATEGORY.options.find(o => o.slug === parsedPathParams.categorySlug)) === null || _CATEGORY$options$fin === void 0 ? void 0 : _CATEGORY$options$fin.value;
      if (categoryEnumValue && CATEGORY) {
        urlParams.set(`${KEY_PREFIX}${String(CATEGORY.field)}`, String(categoryEnumValue));
      }

      // The legacy app marketplace used /apps/:slug for both categories and
      // individual app listings. If the slug doesn't match a category, assume
      // it's a listing slug and redirect to the listing details page.
      if (!categoryEnumValue) {
        return {
          pathname: generatePath(Paths.LISTING_DETAILS, {
            listingSlug: parsedPathParams.categorySlug
          }),
          params: new URLSearchParams(),
          isLegacyListingRedirect: true
        };
      }
    }
    if (productType === 'AGENT' && parsedPathParams.teamSlug) {
      var _AGENT_TEAM$options$f;
      const teamEnumValue = AGENT_TEAM === null || AGENT_TEAM === void 0 || (_AGENT_TEAM$options$f = AGENT_TEAM.options.find(o => o.slug === parsedPathParams.teamSlug)) === null || _AGENT_TEAM$options$f === void 0 ? void 0 : _AGENT_TEAM$options$f.value;
      if (teamEnumValue && AGENT_TEAM) {
        urlParams.set(`${KEY_PREFIX}${String(AGENT_TEAM.field)}`, String(teamEnumValue));
      }
    }
    urlParams.set(`${KEY_PREFIX}PRODUCT_TYPE`, productType);
    if (parsedPathParams.page) {
      urlParams.set(`${KEY_PREFIX}page`, parsedPathParams.page.toString());
    }
    return {
      pathname: Paths.SEARCH_RESULTS,
      params: urlParams,
      isLegacyListingRedirect: false
    };
  }, [AGENT_TEAM, CATEGORY, loading, location.search, parsedPathParams, productType]);
  if (loading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      size: "medium",
      marginBlockStart: "1600"
    });
  }
  if (isLegacyListingRedirect && getIsPrerenderUserAgent()) {
    return /*#__PURE__*/_jsx(PrerenderLegacyListingRedirect, {
      listingSlug: parsedPathParams.categorySlug
    });
  }
  const search = params.toString();
  return /*#__PURE__*/_jsx(Redirect, {
    to: {
      pathname,
      search: search ? `?${search}` : ''
    }
  });
}